<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FormKit } from '@formkit/vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { getTemplateButtonLabels } from 'dashboard/helper/templateHelper';
import { slugifyButtonLabel } from 'dashboard/helper/bitrixSurveyButtonsHelper';

// `values` is the same reactive settings object the parent page keeps for the whole
// integration form (see InboxIntegrationsPage.vue / NewHook.vue). entity/status_field are
// rendered as FormKit fields further down, which bind straight into the enclosing
// `<FormKit type="form" v-model="values">` by field `name` regardless of how deep in the
// component tree they're nested, so this component never needs to touch them itself — FormKit
// tracks those keys because it owns a registered field for each of them.
//
// `survey_buttons` is deliberately NOT stored inside `values` — it has no corresponding
// registered FormKit field (it's rendered by this custom widget instead), and the enclosing
// `<FormKit type="form">` only reliably keeps values keys tied to fields it knows about. A key
// introduced into `values` later, off to the side, gets wiped the next time FormKit resyncs its
// aggregate form value (this bit hard on the account-level "create hook" page, which has enough
// other fields that a resync reliably landed between this widget seeding its rows and the next
// paint). So `survey_buttons` is a genuine `defineModel`, bound to a plain ref the parent keeps
// *outside* `values` entirely, merged into the settings payload only at submit time.
const props = defineProps({
  values: { type: Object, required: true },
  // Raw WhatsApp template object (or null) currently selected via `survey_template_name`, used
  // to pre-fill/sync button labels. Only templates with a BUTTONS component get this far — see
  // `showBitrixSurveyWidget` in the parent.
  template: { type: Object, default: null },
  // FormKit item schemas sourced from apps.yml's `settings_form_schema` (already carry the
  // Spanish labels/help copy the backend maintains) — rendered here instead of duplicating that
  // copy in this file.
  entityField: { type: Object, default: null },
  statusFieldField: { type: Object, default: null },
});

const surveyButtonsModel = defineModel('surveyButtons', {
  type: Array,
  default: () => [],
});

const { t } = useI18n();

const MAX_BUTTONS = 3;
const MIN_BUTTONS = 1;

const activeTabIndex = ref(0);
const tabs = computed(() => [
  {
    label: t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.TABS.STATUS'),
    key: 0,
  },
  {
    label: t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.TABS.BUTTONS'),
    key: 1,
  },
]);
const onTabChanged = tab => {
  activeTabIndex.value = tab.key;
};

// Every button always owns its own `status_field` — there is no shared/global toggle (each row
// is independently editable from the moment it's created). The top-level "Campo de estado a
// actualizar" field (Tab 1) is only used as a convenience default to pre-fill *new* rows.
const blankRow = () => ({
  key: '',
  label: '',
  status_field: props.values.status_field || '',
  status_value: '',
  reply_message: '',
});

const surveyButtons = computed(() =>
  Array.isArray(surveyButtonsModel.value) ? surveyButtonsModel.value : []
);

const setSurveyButtons = rows => {
  surveyButtonsModel.value = rows;
};

const templateLabels = computed(() =>
  props.template ? getTemplateButtonLabels(props.template) : []
);

// Only offered once there's something meaningful to pull in — running it just re-syncs labels
// (up to 3, matching the template's own quick-reply buttons), it never touches an existing
// row's status_field/status_value except by position, so per-button Bitrix config already
// filled in survives a re-sync.
const canSyncWithTemplate = computed(() => templateLabels.value.length > 0);

const syncWithTemplate = () => {
  const labels = templateLabels.value.slice(0, MAX_BUTTONS);
  const current = surveyButtons.value;
  const merged = labels.map((label, index) => ({
    ...(current[index] || blankRow()),
    label,
  }));
  setSurveyButtons(merged.length ? merged : [blankRow()]);
};

// Seed the button list the first time this widget becomes visible for a given hook: prefer
// whatever was already saved (existing hook), then a best-effort migration of the legacy
// SI/NO fields (pre-survey_buttons hooks), then the template's own quick-reply labels, and
// only fall back to one blank row if none of those have anything to offer. Deliberately never
// runs again afterwards (or when just switching templates) so it can't silently clobber an
// admin's already-customized button labels — that's what "Sync with template" is for.
const initButtons = () => {
  if (surveyButtons.value.length) {
    return;
  }

  // Mismo texto histórico que BitrixSurveyResponse::DEFAULT_REPLY_MESSAGES: se usa acá solo
  // como semilla inicial de la migración legacy -> survey_buttons (si el hook ya tenía su
  // propio reply_message_contactado/reply_message_no_contactado, ese gana). El admin puede
  // editarlo libremente una vez migrado, ver campo REPLY_MESSAGE_FIELD más abajo.
  const legacyButtons = [];
  if (props.values.status_contactado) {
    legacyButtons.push({
      ...blankRow(),
      label: 'Sí, confirmo',
      status_value: props.values.status_contactado,
      reply_message:
        props.values.reply_message_contactado ||
        '¡Qué buena noticia! 😊 Muchas gracias por tu respuesta.',
    });
  }
  if (props.values.status_no_contactado) {
    legacyButtons.push({
      ...blankRow(),
      label: 'No puedo',
      status_value: props.values.status_no_contactado,
      reply_message:
        props.values.reply_message_no_contactado ||
        'Lamentamos la demora. Un asesor comercial se estará contactando con vos a la brevedad 😊. Muchas gracias.',
    });
  }
  if (legacyButtons.length) {
    setSurveyButtons(legacyButtons);
    return;
  }

  if (templateLabels.value.length) {
    setSurveyButtons(
      templateLabels.value.slice(0, MAX_BUTTONS).map(label => ({
        ...blankRow(),
        label,
      }))
    );
    return;
  }

  setSurveyButtons([blankRow()]);
};

initButtons();

const canAddRow = computed(() => surveyButtons.value.length < MAX_BUTTONS);
const canRemoveRow = computed(() => surveyButtons.value.length > MIN_BUTTONS);

const addRow = () => {
  if (!canAddRow.value) return;
  setSurveyButtons([...surveyButtons.value, blankRow()]);
};

const removeRow = index => {
  if (!canRemoveRow.value) return;
  setSurveyButtons(surveyButtons.value.filter((_, i) => i !== index));
};
</script>

<template>
  <div
    class="flex flex-col gap-4 p-3 rounded-lg outline outline-1 outline-n-weak"
  >
    <TabBar
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      @tab-changed="onTabChanged"
    />

    <!--
      v-show, not v-if: entity/status_field FormKit fields live inside the outer
      `<FormKit type="form">` (see InboxIntegrationsPage.vue / NewHook.vue) and contribute
      directly to its tracked value. Unmounting a registered FormKit field (v-if) drops it from
      the form's value entirely, which silently stripped entity/status_field from the saved
      settings whenever the Buttons tab was active at save time. v-show keeps both tabs' fields
      mounted at all times.
    -->
    <div v-show="activeTabIndex === 0" class="flex flex-col gap-1">
      <FormKit v-if="entityField" v-bind="entityField" />
      <FormKit v-if="statusFieldField" v-bind="statusFieldField" />
    </div>

    <div v-show="activeTabIndex !== 0" class="flex flex-col gap-3">
      <div class="flex items-start justify-between gap-2">
        <p class="text-xs text-n-slate-11 max-w-md">
          {{
            $t('INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.INTRO')
          }}
        </p>
        <NextButton
          v-if="canSyncWithTemplate"
          type="button"
          sm
          faded
          slate
          icon="i-lucide-refresh-cw"
          :label="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.SYNC_WITH_TEMPLATE'
            )
          "
          class="flex-shrink-0"
          @click="syncWithTemplate"
        />
      </div>

      <div
        v-for="(row, index) in surveyButtons"
        :key="index"
        class="flex flex-col gap-1 p-3 rounded-md outline outline-1 outline-n-weak bg-n-background dark:bg-n-solid-1"
      >
        <div class="flex items-center justify-between">
          <p class="text-xs font-medium text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.ROW_LABEL',
                {
                  n: index + 1,
                }
              )
            }}
          </p>
          <NextButton
            v-tooltip="
              $t(
                'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.REMOVE_BUTTON'
              )
            "
            type="button"
            icon="i-lucide-trash-2"
            sm
            faded
            ruby
            :disabled="!canRemoveRow"
            @click="removeRow(index)"
          />
        </div>

        <FormKit
          :model-value="row.label"
          type="text"
          ignore
          :label="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.LABEL_FIELD.LABEL'
            )
          "
          :placeholder="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.LABEL_FIELD.PLACEHOLDER'
            )
          "
          validation="required"
          @input="
            value =>
              setSurveyButtons(
                surveyButtons.map((r, i) =>
                  i === index ? { ...r, label: value } : r
                )
              )
          "
        />

        <!--
          Solo lectura, a propósito: esta es la key que Bitrix tiene que llamar
          (/webhooks/bitrix/survey_result/<KEY>) -- se recalcula sola a partir del texto de
          arriba (ver bitrixSurveyButtonsHelper.js) cada vez que se guarda, así que mostrarla
          es la única forma de que un admin note que cambió antes de que la Automation Rule
          correspondiente empiece a fallar con "resultado no válido".
          OJO: es una previsualización aproximada -- si dos botones terminan con el mismo texto
          base, el guardado real les agrega un sufijo (_2, _3) para no pisarse, y esta vista no
          lo replica (necesitaría conocer las otras filas, no solo la propia). Evitar labels
          duplicados entre botones de la misma encuesta.
        -->
        <p class="text-xs text-n-slate-11 -mt-1 mb-1">
          {{
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.COMPUTED_KEY',
              { key: slugifyButtonLabel(row.label) }
            )
          }}
        </p>

        <FormKit
          type="text"
          ignore
          :model-value="row.status_field"
          :label="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.STATUS_FIELD_FIELD.LABEL'
            )
          "
          @input="
            value =>
              setSurveyButtons(
                surveyButtons.map((r, i) =>
                  i === index ? { ...r, status_field: value } : r
                )
              )
          "
        />

        <FormKit
          :model-value="row.status_value"
          type="text"
          ignore
          :label="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.STATUS_VALUE_FIELD.LABEL'
            )
          "
          :placeholder="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.STATUS_VALUE_FIELD.PLACEHOLDER'
            )
          "
          validation="required"
          @input="
            value =>
              setSurveyButtons(
                surveyButtons.map((r, i) =>
                  i === index ? { ...r, status_value: value } : r
                )
              )
          "
        />

        <FormKit
          :model-value="row.reply_message"
          type="textarea"
          ignore
          :label="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.REPLY_MESSAGE_FIELD.LABEL'
            )
          "
          :placeholder="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.REPLY_MESSAGE_FIELD.PLACEHOLDER'
            )
          "
          :help="
            $t(
              'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.REPLY_MESSAGE_FIELD.HELP'
            )
          "
          @input="
            value =>
              setSurveyButtons(
                surveyButtons.map((r, i) =>
                  i === index ? { ...r, reply_message: value } : r
                )
              )
          "
        />
      </div>

      <NextButton
        type="button"
        sm
        faded
        icon="i-lucide-plus"
        :label="
          $t(
            'INBOX_MGMT.INBOX_INTEGRATIONS.BITRIX_SURVEY.BUTTONS_TAB.ADD_BUTTON'
          )
        "
        :disabled="!canAddRow"
        class="self-start"
        @click="addRow"
      />
    </div>
  </div>
</template>
